import { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import Colours from './styled/Colours';
import { fontFamilyBold } from './styled/Fonts';
import { btnBg, btnText } from './styled/Theme';
import Loader from './Loader';

interface ButtonStyleProps {
  variant?: string;
  size?: string;
  loading?: boolean | number;
  disabled?: boolean;
}

const buttonStyles = css<ButtonStyleProps>`
  border-radius: 2px;
  height: 3rem;
  ${fontFamilyBold};
  font-size: 14px;
  min-width: 7.5rem;
  letter-spacing: 0.5px;
  padding: 0 1rem;
  text-transform: uppercase;
  transition: box-shadow 0.3s ease-in-out, background-color 0.3s ease-in-out,
    border-color 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(8, 132, 252, 0.3);
  }

  ${({ size }) =>
    size === 'small' &&
    `
    font-size: 12px;
    height: 2rem;
    min-width: 70px;
    padding: 0 0.5rem;
  `}

  ${({ variant, theme }) =>
    variant === 'primary' &&
    `
    background: ${btnBg(theme)};
    border: 1px solid ${btnBg(theme)};
    box-shadow: 0 0.0625rem 0.0625rem 0 rgba(46, 46, 46, 0.2);
    color: ${btnText(theme)};
  `}

  ${({ variant, theme }) =>
    variant === 'secondary' &&
    `
    background: ${Colours.white};
    border: 1px solid ${btnBg(theme)};
    color: ${btnBg(theme)};
  `}

  ${({ loading, theme }) =>
    loading &&
    `
    opacity: 1;
    padding-top: 3px;
    .path {
      stroke: ${btnText(theme)};
    }
  `}

  ${({ disabled }) =>
    disabled &&
    `
      /* Neutral/200 */
      background: #F6F8F9;
      border: 1px solid #F6F8F9;
      /* Neutral/500 (Base) */
      color: #AFC0D0;
  `}

  &:disabled {
    opacity: 0.5;
    /* Neutral/200 */
    background: #f6f8f9;
    border: 1px solid #f6f8f9;
    /* Neutral/500 (Base) */
    color: #afc0d0;
  }
`;

const ThemeButton = styled.button<ButtonStyleProps>`
  ${buttonStyles}
`;

const ThemeButtonLink = styled.a<ButtonStyleProps>`
  ${buttonStyles}
  display: inline-block;
  text-decoration: none;
  line-height: 3rem;

  &:hover {
    text-decoration: none;
  }

  ${({ theme, variant }) =>
    `
    &:hover {
      color: ${variant === 'primary' ? btnText(theme) : btnBg(theme)};
    }
  `}
`;
interface CommonButtonProps extends ButtonStyleProps {
  children?: ReactNode;
  onClick?: () => void | Promise<void>;
}
interface ButtonAsButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>,
    CommonButtonProps {
  href?: undefined;
}
interface ButtonAsLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick'>,
    CommonButtonProps {}

type Props = ButtonAsButtonProps | ButtonAsLinkProps;

const Button = ({
  children,
  variant,
  size,
  id,
  type,
  loading,
  href,
  onClick,
  ...props
}: Props) => {
  if (href) {
    const linkProps = props as ButtonAsLinkProps;
    return (
      <ThemeButtonLink
        href={href}
        id={id}
        variant={variant}
        size={size}
        loading={loading ? 1 : 0}
        {...linkProps}
      >
        {loading ? <Loader /> : children}
      </ThemeButtonLink>
    );
  }

  const buttonProps = props as ButtonAsButtonProps;
  return (
    <ThemeButton
      id={id}
      variant={variant}
      size={size}
      loading={loading ? 1 : 0}
      onClick={() => {
        if (onClick) {
          onClick();
        }
      }}
      {...buttonProps}
    >
      {loading ? <Loader /> : children}
    </ThemeButton>
  );
};

Button.defaultProps = {
  className: '',
  variant: 'primary',
  size: '',
  type: 'button',
  loading: false,
  children: undefined,
  disabled: undefined,
  onClick: undefined,
};

export default Button;
